<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts" setup>
import { DotsVerticalIcon } from 'mdi-vue3'
import { computed, type HTMLAttributes } from 'vue'

import { cn, type DefinesVisibility, isVisible } from '../../../lib/utils'
import { Dropdown } from '../dropdown-menu'
import { type ListItemInterface } from '../list'
import AButton, { type AButtonProperties } from './AButton.vue'

defineOptions({ inheritAttrs: true })

export interface AButtonsItemInterface extends AButtonProperties, DefinesVisibility {
  sticky?: boolean
}

export interface AButtonsProperties {
  /** Makes all buttons sticky, preventing them from collapsing into a menu. */
  class?: HTMLAttributes['class']
  sticky?: boolean
  squish?: boolean
  items?: AButtonsItemInterface[]
  menuLabel?: string
}

interface AButtonsEmits {
  /**
   * Emitted when the button is clicked
   */
  (event: 'click', id: number): void
}

const properties = withDefaults(defineProps<AButtonsProperties>(), {
  sticky: undefined,
  menuLabel: undefined,
  squish: undefined,
  class: 'gap-4',
  items: () => [],
})

defineEmits<AButtonsEmits>()

const classes = computed(() => {
  return cn(['atabix-buttons', properties.class])
})

function isSticky(button: AButtonsItemInterface): boolean {
  return !properties.squish && (properties.sticky || (button.sticky ?? false))
}

const visibleItems = computed(() => {
  return properties.items.filter((item) => isVisible(item))
})

const stickyItems = computed(() => {
  if (properties.squish) {
    return []
  }

  const stickyItems = visibleItems.value.filter((item) => isSticky(item))
  const items = visibleItems.value.length - stickyItems.length <= 1 ? visibleItems.value : stickyItems

  return items.map((item) => {
    return {
      ...item,
      variant: item.variant ?? 'default',
      click: item.click,
    }
  })
})

const collapsedItems = computed(() => {
  if (properties.squish) {
    return visibleItems.value
  }

  const collapsedItems = visibleItems.value.filter((item) => !isSticky(item))

  return collapsedItems.length > 1 ? collapsedItems : []
})

const dropdownItems = computed((): ListItemInterface[] => {
  return collapsedItems.value.map((item) => {
    return {
      title: item.title,
      icon: item.icon,
      iconPosition: item.iconPosition,
      to: item.to,
      click: item.click,
    } as ListItemInterface
  })
})
</script>

<template>
  <div :class="classes">
    <AButton v-for="(button, index) in stickyItems" :key="index" v-bind="button" />
    <Dropdown v-if="collapsedItems.length > 0" :items="dropdownItems">
      <AButton v-if="collapsedItems.length > 0" class="h-10 p-2" variant="outline">
        <DotsVerticalIcon class="size-5" />
        <span v-if="menuLabel" class="atabix-button__menu-label">{{ menuLabel }}</span>
      </AButton>
    </Dropdown>
  </div>
</template>

<style lang="scss">
.atabix-buttons {
  @apply flex;
}
</style>
